import BubbleChartIcon from "@mui/icons-material/BubbleChart";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import IconButton from "@mui/material/IconButton";
import Tooltip from "@mui/material/Tooltip";
import Typography from "@mui/material/Typography";
import * as d3 from "d3";
import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import ApiService from "../../services/ApiService";
import Logger from "../../services/Logger";

const templateUrl = (tplKind, templateId) =>
    tplKind === "cross"
        ? `/v1/puzzle/cross-tpl/${templateId}`
        : `/v1/puzzle/grid-tpl/${templateId}`;

/**
 * Single-line label font size (px) that should fit inside a circle of radius r.
 * Caps by usable diameter vs. approximate text width and line height.
 */
function fontSizeForCircle(r, label, { maxPx = 20, diameterUse = 0.82, charWidthRatio = 0.62, lineHeightRatio = 1.22 } = {}) {
    const text = String(label ?? "");
    const len = Math.max(text.length, 1);
    const usableDiameter = 2 * r * diameterUse;
    const byWidth = usableDiameter / (len * charWidthRatio);
    const byHeight = usableDiameter / lineHeightRatio;
    const fit = Math.min(byWidth, byHeight);
    return Math.min(maxPx, fit);
}

function ClusteredBuggleChartDialog({ open, onClose, templateId, tplKind = "grid" }) {
    const chartHostRef = useRef(null);
    const [dims, setDims] = useState({ w: 640, h: 440 });
    const [loading, setLoading] = useState(false);
    const [fetchError, setFetchError] = useState(null);
    const [bubbleRoot, setBubbleRoot] = useState(null);

    useEffect(() => {
        if (!open || !templateId) return undefined;

        let cancelled = false;
        setLoading(true);
        setFetchError(null);
        setBubbleRoot(null);

        ApiService.getData(templateUrl(tplKind, templateId))
            .then((data) => {
                if (cancelled) return;
                const cb = data?.clusteredBubbles;
                if (cb && Array.isArray(cb.children) && cb.children.length > 0) {
                    setBubbleRoot(cb);
                } else {
                    setBubbleRoot(null);
                    setFetchError("No clusteredBubbles data on this template yet.");
                }
            })
            .catch((err) => {
                Logger.error("Clustered buggle fetch:", err);
                if (!cancelled) {
                    setFetchError(err?.message || "Failed to load template.");
                }
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => {
            cancelled = true;
        };
    }, [open, templateId, tplKind]);

    useLayoutEffect(() => {
        if (!open) return undefined;
        const el = chartHostRef.current;
        if (!el) return undefined;

        let roRaf = null;
        const ro = new ResizeObserver(() => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            roRaf = requestAnimationFrame(() => {
                roRaf = null;
                const w = Math.max(320, el.clientWidth || 640);
                const h = Math.min(520, Math.max(360, w * 0.62));
                setDims({ w, h });
            });
        });
        ro.observe(el);
        return () => {
            if (roRaf != null) cancelAnimationFrame(roRaf);
            ro.disconnect();
        };
    }, [open]);

    useEffect(() => {
        if (!open || loading || !bubbleRoot || !chartHostRef.current) return undefined;

        const container = chartHostRef.current;
        const { w, h } = dims;
        if (w < 32) return undefined;

        container.innerHTML = "";

        const svg = d3
            .select(container)
            .append("svg")
            .attr("width", w)
            .attr("height", h)
            .attr("role", "img")
            .attr("aria-label", "Clustered bubble chart");

        const hierarchy = d3
            .hierarchy(bubbleRoot)
            .sum((d) => (typeof d.value === "number" ? d.value : 0))
            .sort((a, b) => (b.value || 0) - (a.value || 0));

        const pack = d3.pack().size([w - 24, h - 24]).padding(2);
        pack(hierarchy);

        const clusterKey = (d) => {
            let n = d;
            while (n.parent && n.parent.depth > 0) {
                if (n.parent.depth === 1) return String(n.parent.data.name ?? "");
                n = n.parent;
            }
            return n.depth === 1 ? String(n.data.name ?? "") : "cluster";
        };

        const clusters = [
            ...new Set(
                hierarchy
                    .descendants()
                    .filter((d) => d.depth >= 1)
                    .map(clusterKey)
                    .filter(Boolean)
            ),
        ];

        const leaves = hierarchy.leaves();
        const leafValues = leaves.map((l) => l.value || 0);
        const vMin = d3.min(leafValues) ?? 0;
        const vMax = d3.max(leafValues) ?? 1;
        const normLeafValue = (v) => {
            if (vMax - vMin < 1e-9) return 0.5;
            return (v - vMin) / (vMax - vMin);
        };

        const CLUSTER_HUES = [268, 168, 32, 312, 198, 12];
        const hueByCluster = d3.scaleOrdinal(CLUSTER_HUES).domain(clusters.length ? clusters : ["default"]);

        const leafFill = (d) => {
            const t = normLeafValue(d.value || 0);
            const h = hueByCluster(clusterKey(d));
            const sat = 0.38 + 0.48 * t;
            const light = 0.78 - 0.36 * t;
            return d3.hsl(h, sat, light).formatHex();
        };

        /** Shrink only parent (non-leaf) circles visually; leaf sizes unchanged. */
        const PARENT_R_SCALE = 0.74;
        const displayRadius = (d) => (d.children ? d.r * PARENT_R_SCALE : d.r);

        const tx = w / 2 - hierarchy.x;
        const ty = h / 2 - hierarchy.y;
        const gMain = svg.append("g").attr("transform", `translate(${tx},${ty})`);

        const nodes = hierarchy.descendants().filter((d) => d.depth > 0);

        const nodeG = gMain
            .selectAll("g.buggle-node")
            .data(nodes)
            .join("g")
            .attr("class", "buggle-node");

        nodeG
            .append("circle")
            .attr("r", (d) => displayRadius(d))
            .attr("fill", (d) => {
                if (d.children) {
                    const h = hueByCluster(clusterKey(d));
                    return d3.hsl(h, 0.12, 0.94).formatRgb();
                }
                return leafFill(d);
            })
            .attr("stroke", (d) => {
                if (d.children) return "rgba(0,0,0,0.14)";
                const c = d3.color(leafFill(d));
                return c ? c.darker(0.35).formatRgb() : "rgba(0,0,0,0.2)";
            })
            .attr("stroke-width", (d) => (d.children ? 0.9 : 1))
            .attr("opacity", (d) => (d.children ? 0.55 : 0.97));

        const leafLabelMinR = 9;
        nodeG
            .filter((d) => !d.children && d.r > leafLabelMinR)
            .append("text")
            .text((d) => d.data.name ?? "")
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .attr("font-size", (d) => fontSizeForCircle(d.r, d.data.name, { maxPx: 20 }))
            .attr("font-weight", "600")
            .attr("fill", (d) =>
                normLeafValue(d.value || 0) > 0.52 ? "#f8fafc" : "#0f172a"
            )
            .style("pointer-events", "none");

        nodeG
            .filter((d) => d.children && d.depth === 1 && d.r > 18)
            .append("text")
            .text((d) => d.data.name ?? "")
            .attr("text-anchor", "middle")
            .attr("dy", "0.35em")
            .attr("font-size", (d) =>
                fontSizeForCircle(displayRadius(d), d.data.name, {
                    maxPx: 16,
                    charWidthRatio: 0.65,
                    lineHeightRatio: 1.2,
                })
            )
            .attr("font-weight", "700")
            .attr("fill", "rgba(0,0,0,0.55)")
            .style("pointer-events", "none");

        let rafId = 0;
        const start = performance.now();

        const tick = (t) => {
            const elapsed = (t - start) / 1000;
            nodeG.attr("transform", (d) => {
                const label = String(d.data.name ?? d.depth);
                let seed = d.depth * 7919;
                for (let i = 0; i < label.length; i += 1) seed += label.charCodeAt(i) * (i + 3);
                const amp = d.children ? d.r * 0.035 : d.r * 0.065;
                const dx = Math.sin(elapsed * 1.05 + seed * 0.0021) * amp;
                const dy = Math.cos(elapsed * 0.88 + seed * 0.0017) * amp;
                return `translate(${d.x + dx},${d.y + dy})`;
            });
            rafId = requestAnimationFrame(tick);
        };
        rafId = requestAnimationFrame(tick);

        return () => {
            cancelAnimationFrame(rafId);
            d3.select(container).selectAll("*").remove();
        };
    }, [open, loading, bubbleRoot, dims]);

    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Clustered buggle chart</DialogTitle>
            <DialogContent>
                {loading ? (
                    <Box sx={{ display: "flex", alignItems: "center", justifyContent: "center", minHeight: 280, gap: 2 }}>
                        <CircularProgress size={28} />
                        <Typography color="text.secondary">Loading template…</Typography>
                    </Box>
                ) : fetchError && !bubbleRoot ? (
                    <Typography color="error">{fetchError}</Typography>
                ) : (
                    <Box
                        ref={chartHostRef}
                        sx={{
                            width: "100%",
                            minHeight: dims.h,
                            bgcolor: "grey.100",
                            borderRadius: 1,
                            overflow: "hidden",
                        }}
                    />
                )}
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose} variant="contained">
                    Close
                </Button>
            </DialogActions>
        </Dialog>
    );
}

/**
 * @param {string} templateId — grid-tpl or cross-tpl id
 * @param {'grid'|'cross'} tplKind — which API to call
 */
export function BuggleChartTrigger({ templateId, tplKind = "grid" }) {
    const [open, setOpen] = useState(false);
    if (!templateId) return null;

    return (
        <>
            <Tooltip title="Buggle — clustered word bubbles" arrow>
                <IconButton
                    onClick={() => setOpen(true)}
                    sx={{
                        backgroundColor: "rgba(255, 255, 255, 0.9)",
                        borderRadius: "50%",
                        boxShadow: "0 2px 8px rgba(0,0,0,0.1)",
                        flexShrink: 0,
                        "&:hover": {
                            backgroundColor: "rgba(255, 255, 255, 1)",
                        },
                    }}
                >
                    <BubbleChartIcon />
                </IconButton>
            </Tooltip>
            <ClusteredBuggleChartDialog
                open={open}
                onClose={() => setOpen(false)}
                templateId={templateId}
                tplKind={tplKind}
            />
        </>
    );
}

export default ClusteredBuggleChartDialog;
